import { Helmet } from 'react-helmet-async';

/**
 * Per-route SEO tags. Usage:
 *   <SEO title="..." description="..." path="/..." image="..." jsonLd={...} />
 */
export function SEO({
  title,
  description,
  path,
  image = 'https://raiseroot.co/og-image.jpg',
  jsonLd,
  noindex = false,
}) {
  const base = 'https://raiseroot.co';
  const url = path ? `${base}${path}` : base;
  const fullTitle = title ? `${title} | RaiseRoot` : 'RaiseRoot — Affordable Grant Search & Corporate Partnership Tool';
  const desc = description || 'Affordable grant search + corporate partnership matching for small nonprofits. Starting at $19/month. 7-day free trial.';
  const googleVerification = process.env.REACT_APP_GOOGLE_SITE_VERIFICATION;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large" />}
      {googleVerification && <meta name="google-site-verification" content={googleVerification} />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="RaiseRoot" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

// Shared JSON-LD builders
export const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'RaiseRoot',
  url: 'https://raiseroot.co',
  logo: 'https://raiseroot.co/logo.png',
  description: 'Affordable grant search and corporate partnership matching for small nonprofits.',
  sameAs: [],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'info@strategicfundraisingconsulting.com',
    contactType: 'customer support',
  },
};

export const softwareJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'RaiseRoot',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  offers: [
    {
      '@type': 'Offer',
      name: 'Starter',
      price: '19.00',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '19.00',
        priceCurrency: 'USD',
        unitText: 'MON',
      },
    },
    {
      '@type': 'Offer',
      name: 'Growth',
      price: '39.00',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '39.00',
        priceCurrency: 'USD',
        unitText: 'MON',
      },
    },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    reviewCount: '12',
  },
};

export function faqJsonLd(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}
